import React, { useRef, useState } from 'react';
import noop from 'lodash/noop';
import { IconPencil } from '@paretogroup/tess';
import CurrencyInput from '@shared/wrappers/CurrencyInput';
import Loading from '@shared/components/loading/Loading';
import { CLUSTER_FIELDS_FUNCTIONS } from '@domain/dashboard/cluster/cluster.constants';
import { useToasts } from '@shared/toasts';
import { ESC_KEYCODE, EDIT_CURRENCY_FIELD_TEST_IDS, MINIMUM_PERFORMANCE_VALUE_WARNING } from './edit-currency-field.constants';
import { EditCurrencyFieldProps } from './edit-currency-field.types';

import * as S from './edit-currency-field.styles';

interface CurrencyInputRef {
    theInput: {
        focus: () => void,
        blur: () => void,
    }
}

// TODO: Move this component to tess and abstract to allow another text fields in edition
export const EditCurrencyField = ({
    defaultValue = 0,
    onChange = noop,
    currency = 'R$',
    suffix = '',
    dataTestId = EDIT_CURRENCY_FIELD_TEST_IDS.EDIT_CURRENCY_FIELD,
    disabled = false,
    loading = false,
    precision = 2,
    field,
}: EditCurrencyFieldProps) => {
    const inputRef = useRef<CurrencyInputRef>(null);
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState(defaultValue || 0);
    const { showWarningToast } = useToasts();
    const {
        minimumPerformanceValue,
        minimumDailyBudgetValue,
    } = CLUSTER_FIELDS_FUNCTIONS;
    const defaultHiddenFields = [
        minimumPerformanceValue.field,
        minimumDailyBudgetValue.field,
    ];
    const isFormHidden = () => !editing && !value
    && defaultHiddenFields.includes(field);

    const enableEditMode = () => {
        setEditing(true);
        /*
        * This variable and setImmediate is necessary to blur and focus in input element
        * due to a bug in react-currency-input package
        */
        const input = inputRef.current.theInput;
        setImmediate(() => input.focus());
    };

    const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        /*
        * This variable and setImmediate is necessary to blur and focus in input element
        * due to a bug in react-currency-input package
        */
        const input = inputRef.current.theInput;
        setImmediate(() => input.blur());
        onChange(value);
        setEditing(false);
    };

    const handleValueInputted = () => {
        setValue(defaultValue || 0);
        setEditing(false);
    };

    const handleChangeValue = (_, rawValue) => {
        setValue(rawValue);
    };

    const handleKeyUp = (ev) => {
        if (ev.keyCode !== ESC_KEYCODE) return;
        handleValueInputted();
    };

    React.useEffect(() => {
        setValue(defaultValue);
    }, [defaultValue]);

    if (disabled) {
        const ifMinimumPerformanceValueShowWarning = () => (
            field === minimumPerformanceValue.field
                ? showWarningToast({ message: MINIMUM_PERFORMANCE_VALUE_WARNING }) : null
        );

        return (
            <S.DisabledBudgetContent onClick={() => ifMinimumPerformanceValueShowWarning()}>
                <input
                    value="-"
                    disabled={!editing}
                    hidden={!isFormHidden()}
                />
                <form hidden={isFormHidden()}>
                    <CurrencyInput
                        decimalSeparator=","
                        thousandSeparator="."
                        disabled
                        value={value}
                    />
                </form>
            </S.DisabledBudgetContent>
        );
    }

    if (loading) {
        return (
            <S.LoadingBudgetContent>
                <div>{value}</div>
                <Loading height="15px" spinnerSize={15} width="15px" />
            </S.LoadingBudgetContent>
        );
    }

    return (
        <S.EditButtonWrapper
            onClick={enableEditMode}
            editing={editing}
        >
            <input
                value="-"
                disabled={!editing}
                hidden={!isFormHidden()}
            />
            <S.CurrencyWrapper>{currency}</S.CurrencyWrapper>
            <form onSubmit={handleFormSubmit} hidden={isFormHidden()}>
                <CurrencyInput
                    value={value}
                    ref={inputRef}
                    data-testid={dataTestId}
                    disabled={!editing}
                    decimalSeparator=","
                    thousandSeparator="."
                    onChange={handleChangeValue}
                    onBlur={handleValueInputted}
                    onKeyUp={handleKeyUp}
                    precision={precision}
                    suffix={suffix}
                />
            </form>
            <S.EditIconWrapper
                visible={!editing}
            >
                <IconPencil
                    dataTestId={EDIT_CURRENCY_FIELD_TEST_IDS.ICON_PENCIL}
                    size={16}
                />
            </S.EditIconWrapper>
        </S.EditButtonWrapper>
    );
};
